import React, {useEffect, useMemo, useState} from 'react';
import {useTheme} from "@mui/material/styles";
import {useQueryClient} from "@tanstack/react-query";
import {MaterialReactTable, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {Avatar, Box} from "@mui/material";
import {ITotalByPeople} from "../../../../../../utils/interfaces/tannery_raw_material/ITotalByPeople";
import {formatCurrency, getCurrentDate, getFirstDayOfCurrentYear} from "../../../../../../logics/helpers";
import useApiGetTotalsByPeople from "./api-hooks/useApiGetTotalsByPeople";
import {useAuth} from "../../../../../../utils/contexts/auth-context";
import {MRT_Localization_IT} from "material-react-table/locales/it";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableFooterCellProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import {GiCow} from "react-icons/gi";
import MuiReadOnlyBoxBody from "../../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import MuiInputTextField from "../../../../../ui/mui/mui-input/MuiInputTextField";
import Loading from "../../../../../ui/Loading";
import MuiFetchingSpinner from "../../../../../ui/mui/MuiFetchingSpinner";


interface Props {
    onSelect?: (obj: ITotalByPeople | undefined) => void,
    onClose?: () => void;
    alreadySelectedObj?: ITotalByPeople | undefined,
}


const TotalsByPeopleTable = ({onSelect, onClose, alreadySelectedObj}: Props) => {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const {selectedCompany} = useAuth()

    const [selectedRow, setSelectedRow] = useState<ITotalByPeople | undefined>()
    const [rows, setRows] = useState<ITotalByPeople[] | undefined>()

    console.log(getFirstDayOfCurrentYear())

    const [startDate, setStartDate] = useState<string>(getFirstDayOfCurrentYear)
    const [endDate, setEndDate] = useState<string>(getCurrentDate())

    const {
        data,
        isLoading,
        error,
        isFetching,
    } = useApiGetTotalsByPeople(selectedCompany?.company_token, startDate, endDate);


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);


    const columns = useMemo<MRT_ColumnDef<ITotalByPeople>[]>(
        () => [

            {
                accessorKey: 'people__people_name',
                header: 'fornitore',
                enableColumnFilter: true,
                filterFn: 'contains',
                accessorFn: (row) => row.people__people_name,

            },

            {
                accessorKey: 'total_pcs',
                header: 'totale pezzi',
                filterFn: 'contains',
                accessorFn: (row) => formatCurrency(row.total_pcs) + " pcs",
                Footer: () => {
                    const totaleFiltered = table.getFilteredRowModel().rows.reduce(
                        (acc, row) => acc + (row.original.total_pcs ?? 0),
                        0
                    );
                    return <Box>{(formatCurrency(totaleFiltered)) + " pcs"}</Box>;
                },
            },
            {
                accessorKey: 'total_sqm',
                header: 'totale mq',
                filterFn: 'totale mq',
                accessorFn: (row) => row.total_sqm.toFixed(2) + " mq",
                Footer: () => {
                    const totaleFiltered = table.getFilteredRowModel().rows.reduce(
                        (acc, row) => acc + (row.original.total_sqm ?? 0),
                        0
                    );
                    return <Box>{(formatCurrency(totaleFiltered)) + " mq"}</Box>;
                },
            },
            {
                accessorKey: 'total_kgs',
                header: 'totale kg',
                filterFn: 'contains',
                accessorFn: (row) => formatCurrency(row.total_kgs) + " kg",
                Footer: () => {
                    const totaleFiltered = table.getFilteredRowModel().rows.reduce(
                        (acc, row) => acc + (row.original.total_kgs ?? 0),
                        0
                    );
                    return <Box>{(formatCurrency(totaleFiltered)) + " kg"}</Box>;
                },
            },
            {
                accessorKey: 'total_value',
                header: 'valore totale',
                filterFn: 'contains',
                accessorFn: (row) => formatCurrency(row.total_value) + " €",
                Footer: () => {
                    const totaleFiltered = table.getFilteredRowModel().rows.reduce(
                        (acc, row) => acc + (row.original.total_value ?? 0),
                        0
                    );
                    return <Box>{(formatCurrency(totaleFiltered)) + " €"}</Box>;
                },
            },


        ],
        [rows]
    );


    const table = useMaterialReactTable<ITotalByPeople>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        localization: MRT_Localization_IT,
        data: rows ? rows : [],
        columnFilterDisplayMode: 'subheader',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableFooterCellProps: getMuiTableFooterCellProps(theme),
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: false,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
            showColumnFilters: true,
            sorting: [
                {
                    id: 'data_scarico',
                    desc: true
                }
            ],
            columnFilters: [
                {
                    id: 'mq',
                    value: [1, undefined]
                },
            ]
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        // note_marco: onclick sulla row di MRT (onSelect da esterno opzionale (vedi Dichiarazioni Intento Passive Q)
        muiTableBodyRowProps: ({row}) => ({
            ...getMuiTableBodyRowProps(theme), // Spread the existing props
            onClick: () => {
                setSelectedRow(row.original); // Assuming `row.original` contains the actual data
                //handleRowClick(); // Pass the full row data to your click handler
            },
        }),
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <GiCow color={theme.palette.primary.main}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'Totali Acquistato per Fornitore'}/>
                    <Box className={'mx-2'}>
                        <MuiInputTextField
                            onChange={setStartDate}
                            label="data inizio"
                            type="date"
                            value={startDate}
                            required
                        />
                    </Box>
                    <Box className={'mx-2'}>
                        <MuiInputTextField
                            onChange={setEndDate}
                            label="data fine"
                            type="date"
                            value={endDate}
                            required
                        />
                    </Box>

                </>
            </Box>),


    });


    return <>
        {isLoading ? <Loading/> :
            <>


                <MaterialReactTable table={table}/>


                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default TotalsByPeopleTable;
